import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService, NavigateArgs } from 'src/app/wizard-bar/services/wizard.service';
import { of, Observable, forkJoin, Subscription, Subject, scheduled, asyncScheduler } from 'rxjs';
import { UtilityAccountsGridComponent } from 'src/app/shared/components/utility-accounts-grid/utility-accounts-grid.component';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from 'src/app/shared/models/quote-request-state';
import { UtilityInformationService } from 'src/app/shared/services/utility-information.service';
import { Store } from '@ngrx/store';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { LoadingModalComponent, LoadingModalData } from '@app/shared/components/loading-modal/loading-modal.component';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { UtilityAccount } from '@app/shared/models/UtilityAccount';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { debounceTime, filter, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { AppState } from '@app/state/app.state';
import { commodityTypeSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { LoadedUtilityInformation, SetCommodityType, SetContractLengthsSelected, SetUtilityDetails, SetUtilityInformation, SupplierUtilityInformation } from '@utility-information/state/actions/utility-information.actions';
import { isReadOnlySelector, loggedInUserSelector, loggedInUtilityHubIdentitySelector, quoteSelector } from '@app/state/app.selectors';
import { SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { CustomService } from '@app/custom/services/custom.service';
import { UtilityHubService } from '@app/shared/services/utilityhub.service';

@Component({
  selector: 'app-custom-utility-accounts',
  templateUrl: './custom-utility-accounts.component.html',
  styleUrls: ['./custom-utility-accounts.component.scss'],
})
export class CustomUtilityAccountsComponent implements OnInit, OnDestroy {
  public quoteRequestId: any;
  public grid: UtilityAccountsGridComponent;
  public quoteRequestState: QuoteRequestState;
  userIdentity: UtilityHubIdentity;

  private initialFormValue: UtilityAccount[];
  isPriceRequested: boolean;
  ownerUserId: any;
  route$: Subscription;
  dialog$: Subscription;
  selectLoggedInUser$: Subscription;
  uhIdentity$: Subscription;
  saveUtilityAccounts$: Subscription;

  nextButtonClicked: Subject<void> = new Subject<void>();
  commodity: CommodityType;
  commodityType = CommodityType;
  uom: string;
  isReadOnly$: Subscription;
  isReadOnly: boolean;
  quote$: Subscription;
  quote: any;
  private isSaving: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    public quoteService: QuoteRequestService,
    private utilityInformationService: UtilityInformationService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private pricingSourceService: PricingSourceService,
    private cd: ChangeDetectorRef,
    private customService: CustomService,
    public utilityHubService: UtilityHubService
  ) {}

  ngOnDestroy(): void {
    this.route$?.unsubscribe();
    this.dialog$?.unsubscribe();
    this.selectLoggedInUser$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.saveUtilityAccounts$?.unsubscribe();
    this.store.dispatch(new SetContractLengthsSelected(this.quoteRequestId, undefined));
    this.isReadOnly$?.unsubscribe();
    this.quote$?.unsubscribe();
  }

  ngOnInit(): void {
    this.selectLoggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;
        });
      }
    });

    this.wizardService.bindStep(this.route, (args) => this.save(args));
    this.route$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.dialog$ = this.dialog
          .open(LoadingModalComponent, {
            width: '25%',
            disableClose: true,
            data: {
              message: `Loading`,
              unitOfWork: this.load(),
            } as LoadingModalData,
          })
          .afterClosed()
          .subscribe((res) => {
            this.quoteRequestState = res.state;
            this.commodity = res.state.commodity;
            this.uom = res?.state?.uom;
            this.isPriceRequested = res.state.status === QuoteRequestStatus.QuoteRequested;
            this.grid.populate(res.utilityAccounts, res.utilityInformation, res.state, res.quoteConfiguration);
            this.initialFormValue = this.grid.getDataSnapshot();
            this.store.dispatch(new SetQuote(res.state, this.userIdentity, res.state.hasIDontKnows));
          });
      }
    });

    
    this.store.select(commodityTypeSelector).subscribe(c => console.log(c));

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });

    this.quote$ = this.store.select(quoteSelector).pipe(
      tap((quotingModuleState) => {
        this.quote = quotingModuleState.quote;
      }),
      filter(() => this.quoteRequestId && this.quote?.state && this.quote?.quoteRequestType && this.quote?.zipCode && this.quote?.commodity),
      tap(() => {
        this.store.dispatch(
          new SetUtilityInformation(
            this.quoteRequestId,
            this.quote?.state,
            this.quote?.quoteRequestType,
            this.quote?.zipCode,
            this.quote?.commodity
          )
        );
      })
    ).subscribe();
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  public gridInit(grid: UtilityAccountsGridComponent) {
    this.grid = grid;
  }

  private load(): Observable<any> {
    const externalId = this.utilityHubService.getExternalId(this.userIdentity);
    
    return forkJoin({
      state: this.quoteService.getQuoteRequestState(this.quoteRequestId),
      utilityAccounts: this.quoteService.getUtilityAccounts(this.quoteRequestId),
      companyDetails: this.customService.getCompanyDetails(this.quoteRequestId),
      quoteConfiguration: this.customService.getQuoteConfiguration(this.quoteRequestId),
      supplierUtilityInformation: this.utilityHubService.getSupplierUtilityInformation(externalId)
    }).pipe(
      mergeMap((res) => {
        this.quoteRequestState = res.state;
        this.store.dispatch(new SetUtilityDetails(this.quoteRequestId, res.companyDetails.utilityDetails));
        this.store.dispatch(new SupplierUtilityInformation(res.supplierUtilityInformation));

        return this.utilityInformationService.getUtilityInformationForQuote(res.state).pipe(
          map((utilityInformation) => {
            this.store.dispatch(new SetCommodityType(this.quoteRequestId, this.quoteRequestState.commodity));
            return { ...res, utilityInformation };
          })
        );
      })
    );
  }

  public save(args: NavigateArgs): Observable<any> {
    if (this.isReadOnly && !this.userIdentity.isSupplierManagerOrSalesManager) {
      return of(true);
    }

    this.grid.validateAccounts();

    return new Observable((obs) => {
      if (this.isGridDirty()){
        if (this.grid.isValid && !this.isSaving) {
          this.isSaving = true;
          
          scheduled([null], asyncScheduler).pipe(
            debounceTime(500),
          ).subscribe(() => {
            const utilityAccounts = this.grid.getData();
            utilityAccounts.forEach((x) => {
              x.annualUsageUOM = this.uom;
            });

            this.saveUtilityAccounts$ = this.quoteService.saveUtilityAccounts(this.quoteRequestId, utilityAccounts)
            .pipe(
              finalize(() => this.isSaving = false)
            )
            .subscribe(() => {
              obs.next(true);
              obs.complete();
            });
          });      
        } else {
          if (
            (args.direction && args.direction < 0) ||
            this.wizardService.clickedStepIsPreviousStep(CustomConstants.wizardSteps, args.step)
          ) {
            obs.next(true);
          } else {
            obs.next(false);
          }
        }
      } else {
        obs.next(true);
      }
    });
  }

  public next() {
    this.nextButtonClicked.next();
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  public back() {
    this.wizardService.previous(this.isQuoteRequestInvalidated());
  }

  public isQuoteRequestInvalidated() {
    return (
      this.hasFormChanged() && this.quoteRequestState?.status !== QuoteRequestStatus.InProgress && !this.isSupplierManagerValidChange()
    );
  }

  isGridDirty(): boolean {
    // The ticket I'm working on specifies the Matrix route only, so for now I implemented this here.
    // This could go in the utility accounts grid component when the custom route gets this logic,
    // but the definition of dirty here excludes changes from I don't know to a valid value, logic that may not be universal.
    let dirty = false;
    const gridData = this.grid.getData();

    if (gridData.length !== this.initialFormValue?.length) return true;

    if (this.hasFormChanged())
    {
      dirty = true;
    }

    return dirty;
  }

  isSupplierManagerValidChange() {
    if (this.userIdentity.isSupplierManagerOrSalesManager && !this.isGridDirty()) {
      return true;
    }
    return false;
  }

  public hasFormChanged() {
    if (!this.grid) {
      return false;
    }
    return !isEqual(this.initialFormValue, this.grid.getData());
  }

  public isSupplierManagerManualEntry(): Observable<boolean> {
    return new Observable<boolean>((n) => {
      if (!this.quoteRequestState) {
        n.next(false);
      } else {
        this.pricingSourceService
          .getPricingSource(this.quoteRequestState.quoteRequestType, this.quoteRequestState.commodity)
          .subscribe((ps) => {
            n.next(ps.source === PricingSource.ManualEntry && this.userIdentity.isSupplierManagerOrSalesManager);
            n.complete();
          });
      }
    });
  }
}
